import React, { useEffect, useRef, useState } from "react";
import { Cookie, Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "cookie_choice";

/**
 * CookieBanner — glass-morphism, in basso a destra su desktop, full-width su mobile.
 * Animazioni: slide-up (entry), flip 3D bottone (600ms), slide-right (exit).
 */
export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [entering, setEntering] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [flipped, setFlipped] = useState(null); // 'accepted' | 'necessary' | null

  const showTimer = useRef(null);
  const flipTimer = useRef(null);
  const pauseTimer = useRef(null);
  const exitTimer = useRef(null);

  useEffect(() => {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (existing === "accepted" || existing === "necessary") return;
    } catch (_) {}

    showTimer.current = setTimeout(() => {
      setVisible(true);
      setEntering(true);
    }, 800);

    return () => {
      clearTimeout(showTimer.current);
      clearTimeout(flipTimer.current);
      clearTimeout(pauseTimer.current);
      clearTimeout(exitTimer.current);
    };
  }, []);

  const confirm = (value) => {
    if (flipped) return; // prevent double
    setFlipped(value);
    // Flip 3D dura 600ms
    flipTimer.current = setTimeout(() => {
      try { localStorage.setItem(STORAGE_KEY, value); } catch (_) {}
      // Pausa 180ms per apprezzare ✓
      pauseTimer.current = setTimeout(() => {
        setExiting(true);
        // Uscita dura 470ms
        exitTimer.current = setTimeout(() => {
          setVisible(false);
        }, 470);
      }, 180);
    }, 600);
  };

  if (!visible) return null;

  return (
    <div
      className={`cookie-banner ${entering && !exiting ? "is-entering" : ""} ${
        exiting ? "is-exiting-right" : ""
      }`}
      role="dialog"
      aria-live="polite"
      aria-label="Preferenze cookie"
      data-testid="cookie-banner"
    >
      <span className="cookie-glow" aria-hidden="true" />

      {/* Close X */}
      <button
        type="button"
        onClick={() => confirm("necessary")}
        aria-label="Chiudi"
        className="absolute top-3 right-3 p-1.5 rounded-full text-[#f4ead8]/70 hover:text-[#f5c518] hover:bg-white/10 transition-colors"
        data-testid="cookie-banner-close"
        disabled={!!flipped}
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div
          className="shrink-0 w-11 h-11 flex items-center justify-center rounded-2xl"
          style={{
            background: "linear-gradient(135deg,#8b1e1e 0%,#f5c518 100%)",
            boxShadow: "0 6px 18px rgba(139,30,30,0.4)",
          }}
        >
          <Cookie size={22} className="text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold uppercase tracking-[0.14em] text-[0.98rem] text-[#f4ead8]">
            Cookie &amp; privacy
          </h3>
          <p className="mt-1.5 text-[0.82rem] leading-relaxed text-[#e6d9be]/85">
            Usiamo cookie tecnici per il funzionamento del sito. Nessun tracker invasivo.{" "}
            <Link
              to="/cookie"
              className="text-[#f5c518] hover:underline underline-offset-2"
              data-testid="cookie-banner-link"
            >
              Scopri di più
            </Link>
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-5">
        {/* NECESSARY — ghost */}
        <div className={`cookie-flip ${flipped === "necessary" ? "is-flipped" : ""}`}>
          <div className="cookie-flip-inner">
            <button
              type="button"
              className="cookie-flip-face ghost"
              onClick={() => confirm("necessary")}
              disabled={!!flipped}
              data-testid="cookie-banner-necessary"
            >
              Solo necessari
            </button>
            <div className="cookie-flip-back" aria-hidden="true">
              <span>✓ Confermato</span>
            </div>
          </div>
        </div>

        {/* ACCEPT — primary */}
        <div className={`cookie-flip ${flipped === "accepted" ? "is-flipped" : ""}`}>
          <div className="cookie-flip-inner">
            <button
              type="button"
              className="cookie-flip-face primary"
              onClick={() => confirm("accepted")}
              disabled={!!flipped}
              data-testid="cookie-banner-accept"
            >
              <Sparkles size={14} />
              Accetta tutto
            </button>
            <div className="cookie-flip-back" aria-hidden="true">
              <span>✓ Confermato</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
